import { useImageStore } from "entity-store/image-store/use-image-store";
import styles from "./upload-preview.module.css";
import { useEffect, useRef } from "react";
import EXIF from "exif-js";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";

interface UploadPreviewProps {
  imageUrl: string;
  didLoadGPSCoordinates: (coordinates: Coordinate) => void;
}

export const UploadPreview = ({
  imageUrl,
  didLoadGPSCoordinates,
}: UploadPreviewProps) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const { getImageFromCache, saveImageToCache } = useImageStore();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const filename = new URL(imageUrl).pathname.split("/").pop() ?? imageUrl;
      const cachedImage = await getImageFromCache(filename);
      if (cancelled) return;

      if (cachedImage) {
        if (imgRef.current) imgRef.current.src = cachedImage;
        return;
      }
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      if (cancelled) return;

      const objectUrl = URL.createObjectURL(blob);
      if (imgRef.current) imgRef.current.src = objectUrl;

      await saveImageToCache(filename, blob);
    })();

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const imgEl = imgRef.current;
    if (!imgEl) return;

    const handleLoad = () => {
      EXIF.getData(imgEl, function (this: any) {
        const lat = EXIF.getTag(this, "GPSLatitude");
        const lon = EXIF.getTag(this, "GPSLongitude");
        const latRef = EXIF.getTag(this, "GPSLatitudeRef");
        const lonRef = EXIF.getTag(this, "GPSLongitudeRef");
        const altitude = EXIF.getTag(this, "GPSAltitude");
        const dateTimeOriginal = EXIF.getTag(this, "DateTimeOriginal");

        if (lat && lon && latRef && lonRef) {
          const toDecimal = (dms: number[], ref: string) => {
            const [d, m, s] = dms;
            let dec = d + m / 60 + s / 3600;
            if (ref === "S" || ref === "W") dec *= -1;
            return dec;
          };
          const latitude = toDecimal(lat, latRef);
          const longitude = toDecimal(lon, lonRef);

          const coordinate: Coordinate = {
            lat: latitude,
            lng: longitude,
            elevation: altitude ? parseFloat(altitude) : undefined,
            time: dateTimeOriginal
              ? new Date(
                  dateTimeOriginal.replace(/^(\d+):(\d+):(\d+)/, "$1-$2-$3")
                ).getTime()
              : undefined,
          };

          didLoadGPSCoordinates(coordinate);
        }
      });
    };

    imgEl.addEventListener("load", handleLoad);
    return () => imgEl.removeEventListener("load", handleLoad);
  }, []);

  return (
    <img
      ref={imgRef}
      className={styles.previewImage}
      src={imageUrl}
      alt="Upload Preview"
    />
  );
};
